// Developer: Ankit Gupta
import React from 'react';
import { Card, Row, Col, Space } from 'antd';

export const ShimmerBox: React.FC<{
  width?: string | number;
  height?: string | number;
  borderRadius?: number;
  style?: React.CSSProperties;
}> = ({ width = '100%', height = 20, borderRadius = 6, style }) => (
  <div
    className="dp-shimmer"
    style={{
      width,
      height,
      borderRadius,
      ...style,
    }}
  />
);

/**
 * Topography-matched Dashboard Skeleton:
 * 8 KPI cards, 4 chart card panels, and a recent runs table.
 * Zero layout shifts.
 */
export const DashboardSkeleton: React.FC = () => {
  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      {/* 8 KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Col xs={24} sm={12} lg={6} key={`kpi-${i}`}>
            <Card
              size="small"
              style={{
                borderRadius: 12,
                height: 104,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '12px 16px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <ShimmerBox width="55%" height={14} />
                <ShimmerBox width={32} height={32} borderRadius={16} />
              </div>
              <ShimmerBox width="40%" height={24} style={{ marginBottom: 6 }} />
              <ShimmerBox width="65%" height={10} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* 4 Analytics Chart Panels */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={<ShimmerBox width={160} height={16} />}
            style={{ borderRadius: 12, height: 320 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 16 }}>
              <ShimmerBox width="100%" height={180} borderRadius={8} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <ShimmerBox width="20%" height={12} />
                <ShimmerBox width="20%" height={12} />
                <ShimmerBox width="20%" height={12} />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={<ShimmerBox width={160} height={16} />}
            style={{ borderRadius: 12, height: 320 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 16 }}>
              <ShimmerBox width="100%" height={180} borderRadius={8} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <ShimmerBox width="20%" height={12} />
                <ShimmerBox width="20%" height={12} />
                <ShimmerBox width="20%" height={12} />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={<ShimmerBox width={140} height={16} />}
            style={{ borderRadius: 12, height: 300 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12 }}>
              <ShimmerBox width="100%" height={24} />
              <ShimmerBox width="90%" height={24} />
              <ShimmerBox width="75%" height={24} />
              <ShimmerBox width="60%" height={24} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={<ShimmerBox width={140} height={16} />}
            style={{ borderRadius: 12, height: 300 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12 }}>
              <ShimmerBox width="100%" height={24} />
              <ShimmerBox width="90%" height={24} />
              <ShimmerBox width="75%" height={24} />
              <ShimmerBox width="60%" height={24} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Runs Table Card */}
      <Card
        size="small"
        title={<ShimmerBox width={180} height={18} />}
        style={{ borderRadius: 12 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
          <div style={{ display: 'flex', gap: 16, borderBottom: '1px solid var(--dp-line-soft)', paddingBottom: 10 }}>
            <ShimmerBox width="15%" height={14} />
            <ShimmerBox width="35%" height={14} />
            <ShimmerBox width="20%" height={14} />
            <ShimmerBox width="15%" height={14} />
            <ShimmerBox width="15%" height={14} />
          </div>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`row-${i}`} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '6px 0' }}>
              <ShimmerBox width="15%" height={16} />
              <ShimmerBox width="35%" height={16} />
              <ShimmerBox width="20%" height={18} borderRadius={10} />
              <ShimmerBox width="15%" height={14} />
              <ShimmerBox width="15%" height={14} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

/**
 * Topography-matched Run Detail Skeleton:
 * Title, actions, Pipeline DAG ribbon, Overview descriptions, and Run Lines table.
 */
export const DetailSkeleton: React.FC = () => {
  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space size={12}>
          <ShimmerBox width={180} height={28} />
          <ShimmerBox width={90} height={22} borderRadius={12} />
        </Space>
        <Space size={8}>
          <ShimmerBox width={70} height={32} borderRadius={6} />
          <ShimmerBox width={80} height={32} borderRadius={6} />
          <ShimmerBox width={110} height={32} borderRadius={6} />
        </Space>
      </div>

      {/* Pipeline DAG Card */}
      <Card style={{ marginBottom: 16, borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <ShimmerBox width={200} height={18} />
          <ShimmerBox width={120} height={18} />
        </div>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col span={6}><ShimmerBox height={64} borderRadius={8} /></Col>
          <Col span={6}><ShimmerBox height={64} borderRadius={8} /></Col>
          <Col span={6}><ShimmerBox height={64} borderRadius={8} /></Col>
          <Col span={6}><ShimmerBox height={64} borderRadius={8} /></Col>
        </Row>
        <ShimmerBox height={10} borderRadius={5} style={{ marginBottom: 20 }} />
        <div style={{ display: 'flex', justifyContent: 'space-around', padding: '10px 0' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`dag-node-${i}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <ShimmerBox width={24} height={24} borderRadius={12} />
              <ShimmerBox width={60} height={12} />
            </div>
          ))}
        </div>
      </Card>

      {/* Overview Descriptions */}
      <Card style={{ marginBottom: 16, borderRadius: 12 }}>
        <ShimmerBox width={120} height={18} style={{ marginBottom: 16 }} />
        <Row gutter={[16, 12]}>
          <Col span={8}><ShimmerBox height={36} borderRadius={4} /></Col>
          <Col span={8}><ShimmerBox height={36} borderRadius={4} /></Col>
          <Col span={8}><ShimmerBox height={36} borderRadius={4} /></Col>
          <Col span={8}><ShimmerBox height={36} borderRadius={4} /></Col>
          <Col span={8}><ShimmerBox height={36} borderRadius={4} /></Col>
          <Col span={8}><ShimmerBox height={36} borderRadius={4} /></Col>
        </Row>
      </Card>

      {/* Table Card */}
      <Card style={{ borderRadius: 12 }}>
        <ShimmerBox width={140} height={18} style={{ marginBottom: 16 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <ShimmerBox key={`line-${i}`} height={38} borderRadius={6} />
          ))}
        </div>
      </Card>
    </div>
  );
};

/**
 * Reusable Table Skeleton
 */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 5,
}) => {
  return (
    <div style={{ padding: '8px 0', animation: 'fadeIn 0.2s ease-in-out' }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        {Array.from({ length: columns }).map((_, i) => (
          <ShimmerBox key={`th-${i}`} width={`${100 / columns}%`} height={18} />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={`tr-${r}`} style={{ display: 'flex', gap: 12 }}>
            {Array.from({ length: columns }).map((_, c) => (
              <ShimmerBox key={`td-${r}-${c}`} width={`${100 / columns}%`} height={24} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
